import { format } from 'date-fns';
import { v4 } from 'uuid';
import i18n from '@app/i18n';
import { Language, Namespace } from '@data';
import {
  AnswerType,
  BehalfOf,
  Gender,
  ManagementStatus,
  MandatePermissionEnum,
  OverallTrialStatus,
  PendingLocationChangeStatus,
  QuestionType,
  Role,
  SearchScope,
  SexEnum,
} from '@data/sdk/match';
import { countries, europeanCountries } from './countries.constants';
import { locales } from './date-locales.constant';
import { SECONDS } from './seconds.constant';

export { locales };

const languageLabel = (code: string) => (
  <span data-testid={`language-tab-${code.toLowerCase()}`}>{code}</span>
);

export const languageOptions = () => [
  { value: Language.EN, label: languageLabel('EN'), longLabel: i18n.t('language.english') },
  { value: Language.NL, label: languageLabel('NL'), longLabel: i18n.t('language.dutch') },
  { value: Language.ES, label: languageLabel('ES'), longLabel: i18n.t('language.spanish') },
  { value: Language.PT, label: languageLabel('PT'), longLabel: i18n.t('language.portuguese') },
  { value: Language.FR, label: languageLabel('FR'), longLabel: i18n.t('language.french') },
  { value: Language.DE, label: languageLabel('DE'), longLabel: i18n.t('language.german') },
  { value: Language.IT, label: languageLabel('IT'), longLabel: i18n.t('language.italian') },
  { value: Language.CS, label: languageLabel('CS'), longLabel: i18n.t('language.czech') },
  { value: Language.PL, label: languageLabel('PL'), longLabel: i18n.t('language.polish') },
];

export const getWelcomeMessagesList = (isEmbedded: boolean) =>
  isEmbedded
    ? [
        { text: i18n.t('embedded_welcome_messages.let_s_go') },
        {
          // this message will be dynamically translated
          text: 'embedded_welcome_messages.privacy',
          components: [
            <a
              key="terms_of_use"
              target="_blank"
              href={i18n.t('terms_of_use.link')}
              rel="noreferrer"
            ></a>,
            <a
              key="privacy_policy"
              target="_blank"
              href={i18n.t('privacy_policy.link')}
              rel="noreferrer"
            ></a>,
          ],
        },
        { text: i18n.t('embedded_welcome_messages.answer_or_skip') },
        { text: i18n.t('embedded_welcome_messages.we_have_x_trials') },
        { text: i18n.t('embedded_welcome_messages.we_help_you') },
        { text: i18n.t('embedded_welcome_messages.welcome_to_curewiki') },
      ]
    : [
        { text: i18n.t('welcome_messages.when_there_is_match') },
        { text: i18n.t('welcome_messages.please_answer_questions') },
        { text: i18n.t('welcome_messages.welcome_to_curewiki') },
      ];

export const genderOptions = () => [
  { value: Gender.Male, label: i18n.t('gender.male') },
  { value: Gender.Female, label: i18n.t('gender.female') },
  { value: Gender.Other, label: i18n.t('gender.other') },
];

export const searchScopeOptions = () =>
  Object.values(SearchScope).map((value) => ({
    value,
    label: i18n.t(`general_page.search_scope_options.${value}`),
  }));

export const getCountriesOptions = () =>
  Object.entries(countries).map(([translationKey, value]) => ({
    value,
    label: i18n.t(translationKey, { ns: Namespace.COUNTRIES }),
  }));

export const getEuropeanCountriesOptions = () =>
  Object.entries(europeanCountries).map(([translationKey, value]) => ({
    value,
    label: i18n.t(translationKey, { ns: Namespace.COUNTRIES }),
  }));

export const getBooleanAnswerOptions = () => [
  { label: i18n.t('common.yes'), value: 'true' },
  { label: i18n.t('common.no'), value: 'false' },
];

export const questionValueOptions = () => [
  { label: i18n.t('admin:answer_type.boolean'), value: AnswerType.Boolean },
  { label: i18n.t('admin:answer_type.metric'), value: AnswerType.Number },
  { label: i18n.t('admin:answer_type.options'), value: AnswerType.Options },
];

export const questionCriteriaValueOptions = () => [
  { label: i18n.t('admin:answer_type.boolean'), value: AnswerType.Boolean },
  { label: i18n.t('admin:answer_type.metric'), value: AnswerType.Number },
];

export const booleanOptions = () => [
  { label: i18n.t('common.yes'), value: true },
  { label: i18n.t('common.no'), value: false },
];

export const randomId = v4();

export const managementStatusOptions = () =>
  Object.values(ManagementStatus).map((status) => ({
    value: status,
    label: i18n.t(`admin:management_status.${status}.title`),
  }));

export const daysOptions = Array.from({ length: 31 }, (_, index) => ({
  label: index + 1,
  value: index + 1,
}));

export const monthsOptions = () => {
  const currentLocale = i18n.language || 'en';
  const locale: Locale = locales[currentLocale];

  return Array.from({ length: 12 }, (_, index) => {
    return {
      value: index + 1,
      label: format(new Date(0, index), 'MMMM', { locale }),
    };
  });
};

const yearTill = new Date().getFullYear();

export const yearsOptions = Array.from({ length: 120 }, (_, index) => ({
  label: yearTill - index,
  value: yearTill - index,
}));

export const questionTypeOptions = () => [
  { value: QuestionType.Standard, label: i18n.t('admin:question_type.standard') },
  { value: QuestionType.TrialSpecific, label: i18n.t('admin:question_type.trialSpecific') },
  { value: QuestionType.Proposed, label: i18n.t('admin:question_type.proposed') },
  { value: QuestionType.Physician, label: i18n.t('admin:question_type.physician') },
  { value: QuestionType.Distractor, label: i18n.t('admin:question_type.distractor') },
];

export const createTrialGenderOptions = () => [
  { value: SexEnum.Male, label: i18n.t('gender.male', { ns: Namespace.ADMIN }) },
  { value: SexEnum.Female, label: i18n.t('gender.female', { ns: Namespace.ADMIN }) },
  { value: SexEnum.All, label: i18n.t('gender.all', { ns: Namespace.ADMIN }) },
];

export const notificationDigestOptions = () => [
  { value: SECONDS.SECOND, label: i18n.t('notifications_page.digest.immediately') },
  { value: SECONDS.DAY, label: i18n.t('notifications_page.digest.daily') },
  { value: SECONDS.WEEK, label: i18n.t('notifications_page.digest.weekly') },
  { value: SECONDS.TWO_WEEKS, label: i18n.t('notifications_page.digest.every_2_weeks') },
];

export const teamInviteReceiverOptions = () => [
  {
    value: Role.PatientOrg,
    label: i18n.t(`admin:bulk_invite.receiver_options.${Role.PatientOrg}`),
  },
  {
    value: Role.TrialManager,
    label: i18n.t(`admin:bulk_invite.receiver_options.${Role.TrialManager}`),
  },
  {
    value: Role.Sponsor,
    label: i18n.t(`admin:bulk_invite.receiver_options.${Role.Sponsor}`),
  },
];

export const mandatePermissionsOptions = () => [
  {
    value: MandatePermissionEnum.Trialread,
    label: i18n.t('mandate_permissions.trial_read', {
      ns: Namespace.ADMIN,
    }),
  },
  {
    value: MandatePermissionEnum.Trialwrite,
    label: i18n.t('mandate_permissions.trial_write', {
      ns: Namespace.ADMIN,
    }),
  },
];

export const getOverallStatusOptions = () =>
  Object.values(OverallTrialStatus).map((status) => ({
    value: status,
    label: i18n.t(`admin:trials_library.trial.ct_statuses.${status}`),
  }));

export const getBehalfOfOptions = () => [
  { value: BehalfOf.Myself, label: i18n.t('embedded_v2.searching_for_options.myself') },
  { value: BehalfOf.Child, label: i18n.t('embedded_v2.searching_for_options.child') },
  { value: BehalfOf.Patient, label: i18n.t('embedded_v2.searching_for_options.patient') },
  { value: BehalfOf.Friend, label: i18n.t('embedded_v2.searching_for_options.friend') },
  { value: BehalfOf.Parent, label: i18n.t('embedded_v2.searching_for_options.parent') },
  { value: BehalfOf.Partner, label: i18n.t('embedded_v2.searching_for_options.partner') },
  { value: BehalfOf.Other, label: i18n.t('embedded_v2.searching_for_options.other') },
];

export const OTHER_REJECT_REASON = 'other';

export const rejectReasonOptions = () => [
  { value: 'not_interested', label: i18n.t('trials_page.reject_reason.not_interested') },
  { value: 'not_applicable', label: i18n.t('trials_page.reject_reason.not_applicable') },
  { value: 'too_far', label: i18n.t('trials_page.reject_reason.too_far') },
  { value: 'too_risky', label: i18n.t('trials_page.reject_reason.too_risky') },
  {
    value: 'not_understand_trial',
    label: i18n.t('trials_page.reject_reason.not_understand_trial'),
  },
  {
    value: 'dont_like_sponsor',
    label: i18n.t('trials_page.reject_reason.dont_like_sponsor'),
  },
  { value: OTHER_REJECT_REASON, label: i18n.t('trials_page.reject_reason.other') },
];

export const pendingLocationChangeStatusOptions = () => [
  {
    label: i18n.t('admin:trial_page.updates_table.status_label.pending'),
    value: PendingLocationChangeStatus.Pending,
  },
  {
    label: i18n.t('admin:trial_page.updates_table.status_label.approved'),
    value: PendingLocationChangeStatus.Approved,
  },
  {
    label: i18n.t('admin:trial_page.updates_table.status_label.rejected'),
    value: PendingLocationChangeStatus.Rejected,
  },
];
